import { BrowseEventsInLocationSelector } from '@/components/BrowseEventsInLocationSelector';
import { EventsBucket } from '@/components/EventsBucket';
import { filterUnactionableEvents } from '@/shared/utils/filterUnactionableEvents';
import { IconCategoryBrowse } from '@/components/IconCategoryBrowse';
import { RootState } from '@/redux/store';
import { locationChange } from '@/redux/location';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BrowseTabs } from '@/components/BrowseTabs/BrowseTabs';
import { ALL_TAB, FOR_YOU, TABS_CONFIG } from '@/constants/tabConfig';
import styles from './BrowseContainer.module.scss';
import { getLastRecentSearchTerms, getLastRecentViewsList } from '@/components/utils/utils';
import { mergePromotedEvents } from '@/shared/utils/mergePromotedEvents';
import { selectLocale } from '@/redux/app';
import { useAppSelector } from '@/redux/hooks';
import { sdkRequest } from '@eventbrite/legacy-infra.http';
import { getPromotedEventsOptions } from './promotedUtils';
import { EventInteraction, aggregateContext } from '@/utils/tracking';
import { useTracking } from '@/tracking';
import { ForYou } from '@/components/ForYou/ForYou';
import { useClientAuth } from '@/common/useUserPublicId';
import { PROMOTED_EVENTS_RANKS } from '@/constants/promoted';
import type { bboxShape } from '@eventbrite/consumer-search.discover-utils/types';
import { DestinationEvent, FormattedEvent } from '@eventbrite/consumer-search.event-renderer';

interface BrowseContainerProps extends PropsFromRedux {
    userId: string;
}

export function convertBboxToSearchFormat(
    bboxData: bboxShape | null | undefined,
): { top: number; left: number; bottom: number; right: number } | undefined {
    if (!bboxData?.sw?.lat || !bboxData?.ne?.lat) return undefined;

    return {
        top: bboxData.ne.lat,
        left: bboxData.sw.lng,
        bottom: bboxData.sw.lat,
        right: bboxData.ne.lng,
    };
}

export const BrowseContainer = (props: BrowseContainerProps) => {
    const { location, setUserLocation, userId } = props;
    const locale = useAppSelector(selectLocale);
    const [searchDataEvents, setSearchDataEvents] = useState<{ events: DestinationEvent[] }>({
        events: [],
    });
    // we don't want to fetch the events until we have finished getting the location
    // By default after SSr the location will be null, we don't want to fire the call to get default evetns just yet
    // By using isInitialLoad, we wait for that call to be over or failed, and after it is over, we use the response to fetch the events corresponding to the location
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [currentTab, setCurrentTab] = useState<string>(ALL_TAB);
    // The For-you tab owns its own feed (recommendations + its own promoted call), so it hands its rendered list back
    // for ranking instead of going through searchDataEvents.
    const [forYouEvents, setForYouEvents] = useState<FormattedEvent[]>([]);
    const [isPending, startTransition] = useTransition();

    const { authState, publicUserId } = useClientAuth();

    useEffect(() => {
        if (publicUserId) {
            setCurrentTab(FOR_YOU);
        }
    }, [publicUserId]);

    useEffect(() => {
        const fetchUserLocation = async () => {
            try {
                const response = await fetch('/home/api/geo/place_from_request/');
                if (!response.ok) {
                    throw new Error('Failed to fetch user location');
                }
                const { place } = await response.json();
                setUserLocation(place);
            } catch (e) {
                console.error('Error fetching user location:', e);
            } finally {
                setIsInitialLoad(false);
            }
        };
        fetchUserLocation();
    }, []);

    const fetchEventsForTab = async (tabKey: string) => {
        setIsLoading(true);

        if (tabKey === FOR_YOU) {
            setSearchDataEvents({ events: [] });
            setIsLoading(false);
            return;
        }

        try {
            const placeId = location?.placeId;
            const bbox = convertBboxToSearchFormat(location?.bbox);

            const organicEventsReq = fetch(`/home/api/search/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    placeId,
                    tab: tabKey,
                    bbox,
                }),
            });
            const promotedEventsReq = sdkRequest(
                '/promoted/events/',
                getPromotedEventsOptions(location, TABS_CONFIG[currentTab], {
                    recentSearches: getLastRecentSearchTerms(10),
                    recentViews: getLastRecentViewsList(10),
                }),
                '/home',
            );

            const [organicEventsRes, promotedEventsRes] = await Promise.all([
                organicEventsReq.catch(() => {
                    throw new Error('Failed to fetch organic events');
                }),
                promotedEventsReq.catch(() => []),
            ]);

            const organicData = await organicEventsRes.json();
            const promotedData: DestinationEvent[] = Array.isArray(promotedEventsRes)
                ? promotedEventsRes
                : [];

            startTransition(() => {
                // Filter before merging, not after. EventsBucket drops unactionable events at render time, so
                // splicing ads into the unfiltered response puts them at PROMOTED_EVENTS_RANKS in a list that is
                // longer than the one the user sees — every dropped organic event ahead of an ad pulls that ad a
                // slot earlier. Merging the rendered list makes the configured slots the ones that actually ship.
                const organicEvents = filterUnactionableEvents(organicData?.events);
                const promotedEvents = filterUnactionableEvents(promotedData);

                setSearchDataEvents({
                    events: mergePromotedEvents(
                        organicEvents,
                        promotedEvents,
                        PROMOTED_EVENTS_RANKS,
                    ) as DestinationEvent[],
                });
                setIsLoading(false);
            });
        } catch (error) {
            console.error(`Error fetching search data events for tab: ${tabKey}`, error);
            setSearchDataEvents({ events: [] });
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isInitialLoad) {
            fetchEventsForTab(currentTab);
        }
    }, [location.placeId, location.bbox, currentTab, isInitialLoad]);

    const handleTabChange = (tab: string) => {
        startTransition(() => {
            setCurrentTab(tab);
        });
    };

    // Stable identity so ForYou's reporting effect doesn't re-fire on every BrowseContainer render.
    const handleForYouEventsChange = useCallback((events: FormattedEvent[]) => {
        setForYouEvents(events);
    }, []);

    // Rank against the same list the active tab actually renders.
    // EventsBucket drops unactionable events (not on-sale, non-recurrent) before display, so ranking against
    // the raw searchDataEvents makes absolute_rank/rank index a longer list than the user sees and skew positions.
    const renderedEvents = useMemo(
        () =>
            currentTab === FOR_YOU
                ? forYouEvents
                : filterUnactionableEvents(searchDataEvents?.events),
        [currentTab, forYouEvents, searchDataEvents],
    );

    const getTrackingContext = ({ id, event }: EventInteraction) =>
        aggregateContext({
            id,
            event,
            events: renderedEvents,
            userId,
            locale,
            tabKey: currentTab,
            location,
        });
    // Local tracker fans each interaction out to both log_engagement and PicoX
    // (product_location: 'homepage') from one deduped, batched stream.
    const { ref } = useTracking<HTMLDivElement>(getTrackingContext);

    return (
        <div className={styles.root} data-testid="browse-section" ref={ref}>
            <IconCategoryBrowse />
            <hr className={styles.border} />
            <div className={classNames(styles.horizontalGutters, styles.largeMaxWidth)}>
                <BrowseEventsInLocationSelector location={location} />
            </div>
            <hr className={styles.border} />
            <div className={classNames(styles.horizontalGutters, styles.largeMaxWidth)}>
                <BrowseTabs onTabChange={handleTabChange} selectedTab={currentTab} />
                {currentTab !== FOR_YOU ? (
                    <EventsBucket
                        loadingState={{ isFeLoading: isLoading || isPending }}
                        events={searchDataEvents?.events || []}
                        currentTab={currentTab}
                        authState={authState}
                        publicUserId={publicUserId}
                    />
                ) : (
                    <ForYou
                        location={location}
                        // isInitialLoad flips false in the geo call's `finally`, so this reports "we tried", not "we
                        // found one" — a failed lookup releases the feed to render ad-free instead of spinning.
                        isLocationSettled={!isInitialLoad}
                        onRenderedEventsChange={handleForYouEventsChange}
                    />
                )}
            </div>
        </div>
    );
};

const _mapStateToProps = (state: RootState) => ({
    // isAuthenticated: Boolean(state.user.isAuthenticated),
    // isMobile: state.env.isMobile,
    // tld: state.env?.localeInfo?.tld,
    // tabKey: state.content.browseState?.tabKey,
    // loadingState: state.app.loadingState,
    location: state.location,
    // seasonalContent: state.content.seasonal,
    // featureFlags: state.app.featureFlags,
    // useNewHeader: state.header.forceSpecialStyles,
});

const _mapDispatchToProps = {
    // fetchEventsForTab,
    // fetchUserSavedEvents,
    setUserLocation: locationChange,
};

export const connector = connect(_mapStateToProps, _mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const ConnectedBrowseContainer = connector(BrowseContainer);
